/**
 * useRecentAccounts Hook
 *
 * Manages recently selected accounts in localStorage for quick access.
 * Provides efficient tracking of account selections with automatic
 * deduplication and limit enforcement.
 *
 * @see ACCOUNTING_STORAGE_KEYS.RECENT_ACCOUNTS for the localStorage key
 */

import { useCallback, useMemo } from 'react';
import { useLocalStorage, ACCOUNTING_STORAGE_KEYS } from '@/hooks/useLocalStorage';

const DEFAULT_LIMIT = 5;

export interface UseRecentAccountsOptions {
  /** Maximum number of recent accounts to store (default: 5) */
  limit?: number;
}

export interface UseRecentAccountsReturn {
  /** Array of recently used account IDs, most recent first */
  recentAccountIds: number[];
  /** Add an account ID to the recent list */
  addRecentAccount: (accountId: number) => void;
  /** Clear all recent accounts */
  clearRecentAccounts: () => void;
  /** Remove a specific account from recents */
  removeRecentAccount: (accountId: number) => void;
}

/**
 * Hook for managing recently used accounts with localStorage persistence.
 *
 * Features:
 * - Automatically deduplicates entries (moves existing to top)
 * - Enforces configurable limit (default 5)
 * - Persists across page reloads
 * - Syncs across browser tabs
 *
 * @example
 * ```tsx
 * const { recentAccountIds, addRecentAccount, clearRecentAccounts } = useRecentAccounts();
 *
 * // When user selects an account
 * const handleSelect = (accountId: number) => {
 *   addRecentAccount(accountId);
 *   // ... rest of selection logic
 * };
 *
 * // Display recent accounts
 * const recentAccounts = recentAccountIds.map(id =>
 *   accounts.find(a => a.id === id)
 * ).filter(Boolean);
 * ```
 */
export function useRecentAccounts(
  options: UseRecentAccountsOptions = {}
): UseRecentAccountsReturn {
  const { limit = DEFAULT_LIMIT } = options;

  const [recentAccountIds, setRecentAccountIds] = useLocalStorage<number[]>(
    ACCOUNTING_STORAGE_KEYS.RECENT_ACCOUNTS,
    []
  );

  /**
   * Add an account to the recent list.
   * If the account is already in the list, it moves to the top.
   * Maintains the limit by removing oldest entries.
   */
  const addRecentAccount = useCallback(
    (accountId: number) => {
      setRecentAccountIds((prev) => {
        // Remove if already exists (will be added to top)
        const filtered = prev.filter((id) => id !== accountId);
        // Add to top and enforce limit
        return [accountId, ...filtered].slice(0, limit);
      });
    },
    [setRecentAccountIds, limit]
  );

  /**
   * Clear all recent accounts
   */
  const clearRecentAccounts = useCallback(() => {
    setRecentAccountIds([]);
  }, [setRecentAccountIds]);

  /**
   * Remove a specific account from the recent list
   */
  const removeRecentAccount = useCallback(
    (accountId: number) => {
      setRecentAccountIds((prev) => prev.filter((id) => id !== accountId));
    },
    [setRecentAccountIds]
  );

  // Memoize the return object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      recentAccountIds,
      addRecentAccount,
      clearRecentAccounts,
      removeRecentAccount,
    }),
    [recentAccountIds, addRecentAccount, clearRecentAccounts, removeRecentAccount]
  );
}

export default useRecentAccounts;
